import { useEffect, useReducer } from 'react';
import { Refine, Authenticated } from '@refinedev/core';
import { RefineKbar, RefineKbarProvider } from '@refinedev/kbar';
import { ThemedLayout, ErrorComponent } from '@refinedev/antd';
import routerBindings, {
  NavigateToResource,
  CatchAllNavigate,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from '@refinedev/react-router';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';
import { ConfigProvider, App as AntdApp, theme as antdTheme } from 'antd';
import type { ReactNode } from 'react';
import plPL from 'antd/locale/pl_PL';
import 'dayjs/locale/pl';
import '@refinedev/antd/dist/reset.css';
import '@/layout-overrides.css';

import { ThemeModeProvider } from '@/providers/ThemeModeProvider';
import { PublicationDrawerProvider } from '@/components/user_catalog/publications/drawer/PublicationDrawerProvider';
import { useThemeMode } from '@/providers/themeMode';
import { authProvider } from '@/providers/authProvider';
import { dataProvider } from '@/providers/dataProvider';
import { accessControlProvider } from '@/providers/accessControlProvider';
import { useAppNotificationProvider } from '@/providers/notificationProvider';
import i18n, { i18nProvider } from '@/providers/i18nProvider';

import { LoginPage } from '@/pages/Login';
import { DashboardPage } from '@/pages/Dashboard';
import { UserList, UserShow } from '@/pages/iam/users';
import { OAuthAccountList, OAuthAccountShow } from '@/pages/iam/oauth_accounts';
import { OAuthClientList, OAuthClientShow } from '@/pages/iam/oauth_clients';
import { OAuthTokenList, OAuthTokenShow } from '@/pages/iam/oauth_tokens';
import {
  AuthorList,
  AuthorShow,
  AuthorCreate,
  AuthorEdit,
} from '@/pages/user_catalog/authors';
import {
  LectorList,
  LectorShow,
  LectorCreate,
  LectorEdit,
} from '@/pages/user_catalog/lectors';
import {
  PublisherList,
  PublisherShow,
  PublisherCreate,
  PublisherEdit,
} from '@/pages/user_catalog/publishers';
import {
  TagList,
  TagShow,
  TagCreate,
  TagEdit,
} from '@/pages/user_catalog/tags';
import {
  EmotionList,
  EmotionShow,
  EmotionCreate,
  EmotionEdit,
} from '@/pages/user_catalog/emotions';
import {
  SeriesList,
  SeriesShow,
  SeriesCreate,
  SeriesEdit,
} from '@/pages/user_catalog/series';
import {
  BannerActionList,
  BannerActionShow,
  BannerActionCreate,
  BannerActionEdit,
} from '@/pages/user_catalog/banner_actions';
import {
  BannerList,
  BannerShow,
  BannerCreate,
  BannerEdit,
} from '@/pages/user_catalog/banners';
import {
  ManagedCollectionList,
  ManagedCollectionShow,
  ManagedCollectionCreate,
  ManagedCollectionEdit,
} from '@/pages/user_catalog/managed_collections';
import {
  RankingList,
  RankingShow,
  RankingCreate,
  RankingEdit,
} from '@/pages/user_catalog/rankings';
import {
  CollectionList,
  CollectionShow,
} from '@/pages/user_catalog/collections';
import {
  ProfileList,
  ProfileShow,
  FCMTokenList,
  FCMTokenShow,
} from '@/pages/account';
import { IssuedLicencesList } from '@/pages/account/licences/list';
import {
  CategoryList,
  CategoryShow,
  CategoryCreate,
  CategoryEdit,
} from '@/pages/user_catalog/categories';
import { HomePageEdit } from '@/pages/user_catalog/home_page';
import {
  PublicationList,
  PublicationShow,
  PublicationEdit,
} from '@/pages/user_catalog/publications';
import {
  PublicationPremiereList,
  PublicationPremiereShow,
} from '@/pages/user_catalog/publication_premieres';
import { ProductList, ProductShow } from '@/pages/content_catalog/products';
import {
  ContentCatalogPublisherList,
  ContentCatalogPublisherShow,
} from '@/pages/content_catalog/publishers';
import {
  ContentCatalogImprintList,
  ContentCatalogImprintShow,
} from '@/pages/content_catalog/imprints';
import {
  DistributionPeriodList,
  DistributionPeriodShow,
} from '@/pages/content_catalog/distribution_periods';
import { ContentCatalogThemaCodeList } from '@/pages/content_catalog/thema_codes';
import { MasterAssetFileList } from '@/pages/content_catalog/master_asset_files';
import { ProviderImportList } from '@/pages/content_catalog/provider_imports';
import { ReviewList, ReviewShow, ReviewEdit } from '@/pages/feedback/reviews';
import {
  TestimonialList,
  TestimonialShow,
  TestimonialCreate,
  TestimonialEdit,
} from '@/pages/feedback/testimonials';
import {
  ChargesMonthlyList,
  ChargeMonthlyShow,
  CycleList,
  CycleShow,
  RoyaltiesMonthlyList,
  RoyaltyMonthlyShow,
  RoyaltyReportsList,
  RoyaltyReportShow,
} from '@/pages/settlement';
import {
  OrganizationList,
  OrganizationShow,
  OrganizationCreate,
  OrganizationEdit,
  ContractList,
  ContractShow,
  ContractCreate,
  ContractEdit,
} from '@/pages/tenancy';
import { SystemToolsPage } from '@/pages/system/tools';
import { ActivityList, ActivityShow } from '@/pages/system/activities';
import {
  ProgressionList,
  ProgressionShow,
  PositionList,
  PositionShow,
  SegmentList,
  SegmentShow,
  SegmentErrorList,
  SegmentErrorShow,
  SessionList,
  SessionShow,
} from '@/pages/progress';
import {
  EntitlementList,
  EntitlementShow,
  MonetizationProductList,
  MonetizationProductShow,
  MonetizationProductEdit,
  CustomerList,
  CustomerShow,
  RevenueCatBillingPeriodList,
  RevenueCatBillingPeriodShow,
  RevenueCatSubscriptionList,
  RevenueCatSubscriptionShow,
  RevenuecatEventList,
  RevenuecatEventShow,
  WithdrawalContractList,
  WithdrawalContractShow,
} from '@/pages/monetization';

import { ResizableThemedSider } from '@/components/layout/ResizableThemedSider';
import { Header } from '@/components/layout/Header';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { AppTitle } from '@/components/common/AppTitle';

import { resources } from '@/resources';

/** `ConfigProvider` with the algorithm following the theme-mode choice. */
const ThemedConfigProvider = ({ children }: { children: ReactNode }) => {
  const { isDark } = useThemeMode();
  return (
    <ConfigProvider
      locale={plPL}
      theme={{
        algorithm: isDark
          ? antdTheme.darkAlgorithm
          : antdTheme.defaultAlgorithm,
      }}
    >
      {children}
    </ConfigProvider>
  );
};

function App() {
  const [menuEpoch, remountMenu] = useReducer((n: number) => n + 1, 0);

  useEffect(() => {
    const onLanguageChanged = () => remountMenu();
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, []);

  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ThemeModeProvider>
          <ThemedConfigProvider>
            <AntdApp>
              <ErrorBoundary>
                <Refine
                  authProvider={authProvider}
                  dataProvider={dataProvider}
                  accessControlProvider={accessControlProvider}
                  i18nProvider={i18nProvider}
                  notificationProvider={useAppNotificationProvider()}
                  routerProvider={routerBindings}
                  resources={resources}
                  options={{
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: true,
                    projectId: 'novelist-admin',
                    disableTelemetry: true,
                  }}
                >
                  <Routes>
                    <Route
                      element={
                        <Authenticated
                          key="authenticated-layout"
                          fallback={<CatchAllNavigate to="/login" />}
                        >
                          <ThemedLayout
                            Header={() => <Header />}
                            Sider={() => (
                              <ResizableThemedSider
                                key={menuEpoch}
                                fixed
                                Title={({
                                  collapsed,
                                }: {
                                  collapsed: boolean;
                                }) => <AppTitle collapsed={collapsed} />}
                              />
                            )}
                          >
                            <PublicationDrawerProvider>
                              <Outlet />
                            </PublicationDrawerProvider>
                          </ThemedLayout>
                        </Authenticated>
                      }
                    >
                      <Route index element={<DashboardPage />} />

                      {/* IAM Routes */}
                      <Route path="/iam/users">
                        <Route index element={<UserList />} />
                        <Route path=":id" element={<UserShow />} />
                      </Route>

                      <Route path="/iam/oauth_accounts">
                        <Route index element={<OAuthAccountList />} />
                        <Route path=":id" element={<OAuthAccountShow />} />
                      </Route>

                      <Route path="/iam/oauth_clients">
                        <Route index element={<OAuthClientList />} />
                        <Route path=":id" element={<OAuthClientShow />} />
                      </Route>

                      <Route path="/iam/oauth_tokens">
                        <Route index element={<OAuthTokenList />} />
                        <Route path=":id" element={<OAuthTokenShow />} />
                      </Route>

                      {/* Account Routes */}
                      <Route path="/account/profiles">
                        <Route index element={<ProfileList />} />
                        <Route path=":id" element={<ProfileShow />} />
                      </Route>

                      <Route path="/account/licences">
                        <Route index element={<IssuedLicencesList />} />
                      </Route>

                      <Route path="/account/fcm_tokens">
                        <Route index element={<FCMTokenList />} />
                        <Route path=":id" element={<FCMTokenShow />} />
                      </Route>

                      {/* User Catalog Routes */}
                      <Route path="/user_catalog/authors">
                        <Route index element={<AuthorList />} />
                        <Route path="create" element={<AuthorCreate />} />
                        <Route path=":id" element={<AuthorShow />} />
                        <Route path=":id/edit" element={<AuthorEdit />} />
                      </Route>

                      <Route path="/user_catalog/lectors">
                        <Route index element={<LectorList />} />
                        <Route path="create" element={<LectorCreate />} />
                        <Route path=":id" element={<LectorShow />} />
                        <Route path=":id/edit" element={<LectorEdit />} />
                      </Route>

                      <Route path="/user_catalog/publishers">
                        <Route index element={<PublisherList />} />
                        <Route path="create" element={<PublisherCreate />} />
                        <Route path=":id" element={<PublisherShow />} />
                        <Route path=":id/edit" element={<PublisherEdit />} />
                      </Route>

                      <Route path="/user_catalog/tags">
                        <Route index element={<TagList />} />
                        <Route path="create" element={<TagCreate />} />
                        <Route path=":id" element={<TagShow />} />
                        <Route path=":id/edit" element={<TagEdit />} />
                      </Route>

                      <Route path="/user_catalog/emotions">
                        <Route index element={<EmotionList />} />
                        <Route path="create" element={<EmotionCreate />} />
                        <Route path=":id" element={<EmotionShow />} />
                        <Route path=":id/edit" element={<EmotionEdit />} />
                      </Route>

                      <Route path="/user_catalog/series">
                        <Route index element={<SeriesList />} />
                        <Route path="create" element={<SeriesCreate />} />
                        <Route path=":id" element={<SeriesShow />} />
                        <Route path=":id/edit" element={<SeriesEdit />} />
                      </Route>

                      <Route path="/user_catalog/banners">
                        <Route index element={<BannerList />} />
                        <Route path="create" element={<BannerCreate />} />
                        <Route path=":id" element={<BannerShow />} />
                        <Route path=":id/edit" element={<BannerEdit />} />
                      </Route>

                      <Route path="/user_catalog/banner_actions">
                        <Route index element={<BannerActionList />} />
                        <Route path="create" element={<BannerActionCreate />} />
                        <Route path=":id" element={<BannerActionShow />} />
                        <Route path=":id/edit" element={<BannerActionEdit />} />
                      </Route>

                      <Route path="/user_catalog/managed_collections">
                        <Route index element={<ManagedCollectionList />} />
                        <Route
                          path="create"
                          element={<ManagedCollectionCreate />}
                        />
                        <Route path=":id" element={<ManagedCollectionShow />} />
                        <Route
                          path=":id/edit"
                          element={<ManagedCollectionEdit />}
                        />
                      </Route>

                      <Route path="/user_catalog/rankings">
                        <Route index element={<RankingList />} />
                        <Route path="create" element={<RankingCreate />} />
                        <Route path=":id" element={<RankingShow />} />
                        <Route path=":id/edit" element={<RankingEdit />} />
                      </Route>

                      <Route path="/user_catalog/collections">
                        <Route index element={<CollectionList />} />
                        <Route path=":id" element={<CollectionShow />} />
                      </Route>

                      <Route path="/user_catalog/home_page">
                        <Route index element={<HomePageEdit />} />
                      </Route>

                      <Route path="/user_catalog/categories">
                        <Route index element={<CategoryList />} />
                        <Route path="create" element={<CategoryCreate />} />
                        <Route path=":id" element={<CategoryShow />} />
                        <Route path=":id/edit" element={<CategoryEdit />} />
                      </Route>

                      <Route path="/user_catalog/publications">
                        <Route index element={<PublicationList />} />
                        <Route path=":id" element={<PublicationShow />} />
                        <Route path=":id/edit" element={<PublicationEdit />} />
                      </Route>

                      <Route path="/user_catalog/publication_premieres">
                        <Route index element={<PublicationPremiereList />} />
                        <Route
                          path=":id"
                          element={<PublicationPremiereShow />}
                        />
                      </Route>

                      {/* Content Catalog Routes */}
                      <Route path="/content_catalog/products">
                        <Route index element={<ProductList />} />
                        <Route path=":id" element={<ProductShow />} />
                      </Route>

                      <Route path="/content_catalog/publishers">
                        <Route
                          index
                          element={<ContentCatalogPublisherList />}
                        />
                        <Route
                          path=":id"
                          element={<ContentCatalogPublisherShow />}
                        />
                      </Route>

                      <Route path="/content_catalog/imprints">
                        <Route index element={<ContentCatalogImprintList />} />
                        <Route
                          path=":id"
                          element={<ContentCatalogImprintShow />}
                        />
                      </Route>

                      <Route path="/content_catalog/distribution_periods">
                        <Route index element={<DistributionPeriodList />} />
                        <Route
                          path=":id"
                          element={<DistributionPeriodShow />}
                        />
                      </Route>

                      <Route path="/content_catalog/thema_codes">
                        <Route
                          index
                          element={<ContentCatalogThemaCodeList />}
                        />
                      </Route>

                      <Route path="/content_catalog/master_asset_files">
                        <Route index element={<MasterAssetFileList />} />
                      </Route>

                      <Route path="/content_catalog/provider_imports">
                        <Route index element={<ProviderImportList />} />
                      </Route>

                      {/* Monetization Routes */}
                      <Route path="/monetization/entitlements">
                        <Route index element={<EntitlementList />} />
                        <Route path=":id" element={<EntitlementShow />} />
                      </Route>
                      <Route path="/monetization/customers">
                        <Route index element={<CustomerList />} />
                        <Route path=":id" element={<CustomerShow />} />
                      </Route>
                      <Route path="/monetization/products">
                        <Route index element={<MonetizationProductList />} />
                        <Route
                          path=":id"
                          element={<MonetizationProductShow />}
                        />
                        <Route
                          path=":id/edit"
                          element={<MonetizationProductEdit />}
                        />
                      </Route>
                      <Route path="/monetization/revenuecat_subscriptions">
                        <Route index element={<RevenueCatSubscriptionList />} />
                        <Route
                          path=":id"
                          element={<RevenueCatSubscriptionShow />}
                        />
                      </Route>
                      <Route path="/monetization/revenuecat_billing_periods">
                        <Route
                          index
                          element={<RevenueCatBillingPeriodList />}
                        />
                        <Route
                          path=":id"
                          element={<RevenueCatBillingPeriodShow />}
                        />
                      </Route>
                      <Route path="/monetization/revenuecat_events">
                        <Route index element={<RevenuecatEventList />} />
                        <Route path=":id" element={<RevenuecatEventShow />} />
                      </Route>
                      <Route path="/monetization/withdrawal_contracts">
                        <Route index element={<WithdrawalContractList />} />
                        <Route
                          path=":id"
                          element={<WithdrawalContractShow />}
                        />
                      </Route>

                      {/* Feedback Routes */}
                      <Route path="/feedback/reviews">
                        <Route index element={<ReviewList />} />
                        <Route path=":id" element={<ReviewShow />} />
                        <Route path=":id/edit" element={<ReviewEdit />} />
                      </Route>
                      <Route path="/feedback/testimonials">
                        <Route index element={<TestimonialList />} />
                        <Route path=":id" element={<TestimonialShow />} />
                        <Route path="create" element={<TestimonialCreate />} />
                        <Route path=":id/edit" element={<TestimonialEdit />} />
                      </Route>

                      {/* Progress Routes */}
                      <Route path="/progress/progressions">
                        <Route index element={<ProgressionList />} />
                        <Route path=":id" element={<ProgressionShow />} />
                      </Route>
                      <Route path="/progress/positions">
                        <Route index element={<PositionList />} />
                        <Route path=":id" element={<PositionShow />} />
                      </Route>
                      <Route path="/progress/segments">
                        <Route index element={<SegmentList />} />
                        <Route path=":id" element={<SegmentShow />} />
                      </Route>
                      <Route path="/progress/segment_errors">
                        <Route index element={<SegmentErrorList />} />
                        <Route path=":id" element={<SegmentErrorShow />} />
                      </Route>
                      <Route path="/progress/sessions">
                        <Route index element={<SessionList />} />
                        <Route path=":id" element={<SessionShow />} />
                      </Route>

                      {/* Settlement Routes */}
                      <Route path="/settlement/cycles">
                        <Route index element={<CycleList />} />
                        <Route path=":id" element={<CycleShow />} />
                      </Route>
                      <Route path="/settlement/charges_monthly">
                        <Route index element={<ChargesMonthlyList />} />
                        <Route path=":id" element={<ChargeMonthlyShow />} />
                      </Route>
                      <Route path="/settlement/royalties_monthly">
                        <Route index element={<RoyaltiesMonthlyList />} />
                        <Route path=":id" element={<RoyaltyMonthlyShow />} />
                      </Route>
                      <Route path="/settlement/royalty_reports">
                        <Route index element={<RoyaltyReportsList />} />
                        <Route path=":id" element={<RoyaltyReportShow />} />
                      </Route>

                      {/* Tenancy Routes */}
                      <Route path="/tenancy/organizations">
                        <Route index element={<OrganizationList />} />
                        <Route path="create" element={<OrganizationCreate />} />
                        <Route path=":id" element={<OrganizationShow />} />
                        <Route path=":id/edit" element={<OrganizationEdit />} />
                      </Route>
                      <Route path="/tenancy/contracts">
                        <Route index element={<ContractList />} />
                        <Route path="create" element={<ContractCreate />} />
                        <Route path=":id" element={<ContractShow />} />
                        <Route path=":id/edit" element={<ContractEdit />} />
                      </Route>

                      {/* System Routes */}
                      <Route path="/system/activities">
                        <Route index element={<ActivityList />} />
                        <Route path=":id" element={<ActivityShow />} />
                      </Route>
                      <Route path="/system/tools">
                        <Route index element={<SystemToolsPage />} />
                      </Route>

                      <Route path="*" element={<ErrorComponent />} />
                    </Route>

                    <Route
                      element={
                        <Authenticated
                          key="authenticated-login"
                          fallback={<Outlet />}
                        >
                          <NavigateToResource />
                        </Authenticated>
                      }
                    >
                      <Route path="/login" element={<LoginPage />} />
                    </Route>
                  </Routes>

                  <RefineKbar />
                  <UnsavedChangesNotifier />
                  <DocumentTitleHandler />
                </Refine>
              </ErrorBoundary>
            </AntdApp>
          </ThemedConfigProvider>
        </ThemeModeProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
